import React from "react";
import { createPortal } from "react-dom";
import { useGame, useShellVersion } from "../../useShell";
import {
  BenchArrangeView,
  CarriedTool as CarriedToolState,
  HAND_HOTSPOT,
  HAND_ICON_PX,
  ToolFlight as ToolFlightState,
  TOOL_FLIGHT_SECONDS,
} from "../../scenes/bench/BenchArrangeView";
import { HammerCursor } from "../../../game/bench-work/hammer-gesture";
import { BenchAssemblyView } from "../../scenes/bench/BenchAssemblyView";
import { BenchDive, BenchHand } from "../../scenes/bench/BenchDive";
import { BenchGlueView } from "../../scenes/bench/BenchGlueView";
import { BenchPryView } from "../../scenes/bench/BenchPryView";
import { BenchSawView } from "../../scenes/bench/BenchSawView";
import { BenchStrokeView } from "../../scenes/bench/BenchStrokeView";
import { BenchTrayView } from "../../scenes/bench/BenchTrayView";
import { TRAY_SLOT_H, TRAY_SLOT_W } from "../../scenes/bench/trayMath";
import { TUTORIAL_TARGET_ATTRIBUTE } from "../../../game/tutorial";
import { benchStage, benchWork } from "../../scenes/bench/benchStage";
import { BlueprintSlot } from "../../../game/bench-work/blueprint";
import { describeMaterialRequirement } from "../../../game/material-helpers";
import { GLUE_BOTTLE_ICON, toolIconSrc } from "../../../utils/uiImages";

/**
 * What the bench is doing, written onto the page. Nothing here is drawn
 * — the surface is on the canvas — but the work happens in world pixels,
 * and a spec driving the pointer needs to know where an inch of bench
 * lands on screen and what the run made of the last gesture. The old
 * scene published the same names off its own DOM stage; this is that
 * seam, kept as one element so the mapping lives in one place.
 *
 * Read it as: `bench-work` says what job is on (and how far along),
 * `bench-stage` says where the run sits and what each gesture is
 * holding — including where the trays' ledges and tiles are on screen
 * (`data-shelf-tray`, `data-arms-tray`, `data-tray-tiles`) and where a
 * carried piece would land (`data-drop-target`, and `data-drop-slot`
 * for the tray slot lit under it).
 */

/** A screen point as "x,y", or nothing. */
function point(at: { x: number; y: number } | null | undefined): string {
  return at ? `${Math.round(at.x)},${Math.round(at.y)}` : "";
}
export const BenchStageMarker: React.FC = () => {
  const game = useGame();
  useShellVersion();
  const dive = game.entities.tryGetSingleton(BenchDive);
  const stage = benchStage(game);
  if (!dive || dive.openBenchKey === null || !stage) return null;

  const work = benchWork(game);
  const stroke = game.entities.tryGetSingleton(BenchStrokeView);
  const saw = game.entities.tryGetSingleton(BenchSawView);
  const glue = game.entities.tryGetSingleton(BenchGlueView);
  const assembly = game.entities.tryGetSingleton(BenchAssemblyView);
  const arrange = game.entities.tryGetSingleton(BenchArrangeView);
  const pry = game.entities.tryGetSingleton(BenchPryView);
  const trays = game.entities.tryGetSingleton(BenchTrayView);

  const glueRun = glue?.glueProgress() ?? null;
  const build = assembly?.assemblyProgress() ?? null;
  const palletCorner = pry?.palletCorner() ?? null;
  const productCorner = assembly?.productCorner() ?? null;

  // What kind of job the run is on. A running operation names itself;
  // otherwise it's whatever the bench is set up for — a drawing out, a
  // run in the clamps, a pallet staged — and idle when it's just wood
  // lying on a top.
  const script =
    work?.script.kind ??
    (build ? "assembly" : glueRun ? "glue" : palletCorner ? "pry" : "idle");

  // One number for "how far along", whichever gesture is running.
  const progress =
    stroke?.strokeProgress() ??
    saw?.kerfProgress() ??
    (build && build.fasteners > 0 ? build.driven / build.fasteners : null);

  const inches = (value: number) => value.toFixed(2);

  return (
    <div
      className="pointer-events-none fixed inset-0"
      data-testid="bench-work"
      data-script={script}
      data-progress={progress === null ? undefined : progress.toFixed(3)}
      data-zoom={
        dive.closingKey !== null ? "out" : dive.settled() ? "open" : "in"
      }
    >
      <div
        // Nothing here may catch the pointer: the hand belongs to the
        // work surface on the canvas underneath.
        className="pointer-events-none absolute inset-0"
        data-testid="bench-stage"
        data-px-per-in={stage.fit.pxPerIn.toFixed(4)}
        data-origin-x={stage.fit.originX.toFixed(2)}
        data-origin-y={stage.fit.originY.toFixed(2)}
        data-pallet-x={palletCorner ? inches(palletCorner.xIn) : undefined}
        data-pallet-y={palletCorner ? inches(palletCorner.yIn) : undefined}
        data-product-x={productCorner ? inches(productCorner.xIn) : undefined}
        data-product-y={productCorner ? inches(productCorner.yIn) : undefined}
        data-seated={build ? build.seated : undefined}
        data-driven={build ? build.driven : undefined}
        data-hovered={arrange?.handPieceId() ?? ""}
        data-drop-target={arrange?.dropTargetKind() ?? ""}
        data-drop-hook={arrange?.dropHookIndex() ?? ""}
        data-tray-open={trays?.openTray() ?? ""}
        data-drop-slot={
          trays?.dropSlot()
            ? `${trays.dropSlot()!.tray}:${trays.dropSlot()!.index}`
            : ""
        }
        data-shelf-tray={point(trays?.ledgeCenter("shelf"))}
        data-arms-tray={point(trays?.ledgeCenter("arms"))}
        data-tray-tiles={JSON.stringify(
          (trays?.tilePoints() ?? []).map((tile) => ({
            tray: tile.tray,
            id: tile.id,
            x: Math.round(tile.x),
            y: Math.round(tile.y),
          })),
        )}
        data-snap-candidate={assembly?.snapCandidate() ?? ""}
        data-work-hover={stroke?.hoveredOperationId() ?? ""}
        data-glue-run={glueRun ? glueRun.pieces : undefined}
        data-glue-op={glueRun ? glueRun.operationId : undefined}
        data-glue-seams={
          glueRun ? `${glueRun.seamsGlued}/${glueRun.seams}` : undefined
        }
        data-glue-clamps={
          glueRun ? `${glueRun.clamps}/${glueRun.needed}` : undefined
        }
        data-clamps-placed={glue?.clampsSetOut()}
      />
      {/* The tutorial points at a tool's tile in the arms tray — a
          canvas thing — through a mark laid over its slot, measured like
          any chrome target (TutorialSpotlightLayer). */}
      {(trays?.tilePoints() ?? [])
        .filter((tile) => tile.tray === "arms" && tile.toolId)
        .map((tile) => (
          <div
            key={tile.id}
            className="pointer-events-none fixed"
            {...{ [TUTORIAL_TARGET_ATTRIBUTE]: `bench-arms-${tile.toolId}` }}
            style={{
              left: tile.x - TRAY_SLOT_W / 2,
              top: tile.y - TRAY_SLOT_H / 2,
              width: TRAY_SLOT_W,
              height: TRAY_SLOT_H,
            }}
          />
        ))}
      <SlotTag slot={assembly?.hoveredSlot() ?? null} />
      <CarriedTool tool={arrange?.carriedTool() ?? null} />
      <ToolFlight flight={arrange?.toolFlight() ?? null} />
      <HandCursor
        hand={dive.held}
        hammer={assembly?.hammerCursor() ?? pry?.hammerCursor() ?? null}
      />
    </div>
  );
};

/**
 * Whatever's off the rail riding the pointer — the hand IS the hammer,
 * or the glue bottle, while one is up. The native cursor hides under it
 * (BenchDive owns that). A driver in hand shows the gesture's state
 * (`hammer-gesture.ts`): it sits low right after a swing and comes back
 * up over the wind-up, with a glint when it's ready for a full blow;
 * pressing cocks it a little higher; a swing plays its tap; holding
 * turns it over into the claw, which tilts along the lever as the pull
 * comes. The clamp is the one hand with no icon here: its ghost bar on
 * the canvas (BenchGlueView) already rides the pointer.
 *
 * Portaled onto the body, above the HUD root's stack: the icon is
 * still rendered from here, on the overlay root's every-frame cadence,
 * but it has to pass over the tool rail and the rest of the chrome on
 * its way to hang the tool back up — under the HUD it would duck
 * behind them.
 */
const HandCursor: React.FC<{
  hand: BenchHand | null;
  hammer: { cursor: HammerCursor; swings: number; swungAt: number } | null;
}> = ({ hand, hammer }) => {
  const game = useGame();
  if (!hand || hand.kind === "clamp") return null;
  const [x, y] = game.io.mousePosition;
  const now = performance.now();
  const swinging = hammer !== null && now - hammer.swungAt < 240;
  // Just back up after a swing: the glint plays once
  const ready =
    hammer !== null &&
    hammer.cursor.windUp >= 1 &&
    hammer.swungAt !== -Infinity &&
    now - hammer.swungAt < 1200;
  const rotate = hammer
    ? hammer.cursor.phase === "claw"
      ? 180 + 28 * hammer.cursor.pull
      : hammer.cursor.phase === "cocked"
        ? -14
        : 22 * (1 - hammer.cursor.windUp)
    : 0;
  return createPortal(
    <div
      className="pointer-events-none fixed left-0 top-0 z-20"
      data-testid="bench-hand-cursor"
      data-hammer={hammer?.cursor.phase}
      data-wind-up={hammer ? hammer.cursor.windUp.toFixed(2) : undefined}
      style={{
        transform: `translate(${x - HAND_HOTSPOT.x}px, ${y - HAND_HOTSPOT.y}px)`,
      }}
    >
      <img
        key={swinging ? hammer.swings : "still"}
        src={hand.kind === "tool" ? toolIconSrc(hand.toolId) : GLUE_BOTTLE_ICON}
        alt=""
        draggable={false}
        style={{
          width: HAND_ICON_PX,
          height: HAND_ICON_PX,
          rotate: `${rotate}deg`,
          transformOrigin: "12px 11px",
          transition: swinging ? undefined : "rotate 90ms ease-out",
        }}
        className={`select-none [image-rendering:pixelated] drop-shadow-[0_4px_5px_rgba(0,0,0,0.5)] ${
          swinging ? "bench-drive-tap" : ready ? "bench-hammer-ready" : ""
        }`}
      />
    </div>,
    document.body,
  );
};

/**
 * A tool the bare hand is carrying, riding the pointer as chrome: the
 * rail it goes to is DOM over the canvas, so a sprite under the chrome
 * would vanish behind the rail on its way to a hook (see
 * BenchArrangeView's note). Portaled onto the body like the hand
 * cursor, for the same reason. It is the hook's size over a hook, a
 * tray tile's size over a tray, and the hand cursor's — at the hand
 * cursor's own spot — in the open, so a tool let go into the hand
 * doesn't jump as the cursor takes over; it eases between the three.
 */
const CarriedTool: React.FC<{ tool: CarriedToolState | null }> = ({ tool }) => {
  if (!tool) return null;
  return createPortal(
    <div
      className="pointer-events-none fixed left-0 top-0 z-20"
      data-testid="bench-carried-tool"
      style={{
        transform: `translate(${tool.x}px, ${tool.y}px)`,
      }}
    >
      <img
        src={toolIconSrc(tool.toolId)}
        alt=""
        draggable={false}
        className="select-none [image-rendering:pixelated] drop-shadow-[0_4px_5px_rgba(0,0,0,0.5)] transition-[width,height] duration-150"
        style={{ width: tool.size, height: tool.size }}
      />
    </div>,
    document.body,
  );
};

/**
 * A tool's last leg to a hook on the rail (or back to the one it came
 * off), after the hand let it go: the icon slides from the pointer to
 * the hook's middle and the rail's own icon takes over underneath.
 * Keyed per flight so each plays from its start.
 */
const ToolFlight: React.FC<{ flight: ToolFlightState | null }> = ({
  flight,
}) => {
  if (!flight) return null;
  const half = flight.size / 2;
  return createPortal(
    <div
      key={flight.id}
      className="bench-tool-fly pointer-events-none fixed left-0 top-0 z-20"
      data-testid="bench-tool-flight"
      style={
        {
          "--from-x": `${flight.fromX - half}px`,
          "--from-y": `${flight.fromY - half}px`,
          "--to-x": `${flight.toX - half}px`,
          "--to-y": `${flight.toY - half}px`,
          "--fly-duration": `${Math.round(TOOL_FLIGHT_SECONDS * 1000)}ms`,
        } as React.CSSProperties
      }
    >
      <img
        src={toolIconSrc(flight.toolId)}
        alt=""
        draggable={false}
        className="select-none [image-rendering:pixelated] drop-shadow-[0_4px_5px_rgba(0,0,0,0.5)]"
        style={{ width: flight.size, height: flight.size }}
      />
    </div>,
    document.body,
  );
};

/**
 * The tag beside the cursor over an empty outline: what stock the
 * drawing wants there, and whether it goes in standing up. Read before
 * anything is picked up, which is the point — the outline alone doesn't
 * say what fills it.
 */
const SlotTag: React.FC<{ slot: BlueprintSlot | null }> = ({ slot }) => {
  const game = useGame();
  if (!slot) return null;
  const [x, y] = game.io.mousePosition;
  return (
    <div
      data-testid="slot-tip"
      className="pointer-events-none absolute left-0 top-0 z-10"
      style={{ transform: `translate(${x + 14}px, ${y + 16}px)` }}
    >
      <div className="whitespace-nowrap rounded bg-ink-black/80 px-2 py-1 font-condensed uppercase tracking-[0.12em] text-[0.62rem] text-paper-manila shadow-lg">
        <span className="font-semibold">{slot.role}</span>
        {" — "}
        {describeMaterialRequirement(slot.requirement)}
        {slot.onEdge && (
          <span className="text-gold-light"> · stood on edge (F)</span>
        )}
        {slot.onEnd && (
          <span className="text-gold-light"> · stood on end (F)</span>
        )}
      </div>
    </div>
  );
};
